import * as THREE from 'three';
import { Arena } from './Arena.js';
import { Base } from './Base.js';
import { PLAYER_COLORS, BASE_POSITIONS } from '../../../utils/constants.js';

// A permanent clear night. The moon hangs low in the south-west so the default
// view is cross-lit; the aurora and the sky hue drift slowly through a match.
const MOON_DIRECTION = new THREE.Vector3(-0.55, 0.62, 0.56).normalize();
const AURORA_CYCLE_SECONDS = 240;

const SHADOW_EXTENT = 85;
const SHADOW_MAP_SIZE = 2048;

export class Scene {
  constructor() {
    this.scene = new THREE.Scene();
    this.bases = [];
    this.matchClock = null;
    this.ambient = { aurora: 0.6, hueShift: 0, moonDirection: MOON_DIRECTION };
    this.setupLighting();
    this.createArena();
  }

  setupLighting() {
    this.scene.fog = new THREE.Fog(0x141a36, 170, 760);

    // Cool fill from the sky, a deep violet bounce from the ground
    this.hemiLight = new THREE.HemisphereLight(0xb4c0e8, 0x2a2836, 1.4);
    this.scene.add(this.hemiLight);

    // Moonlight, whose shadow box follows the action
    this.moon = new THREE.DirectionalLight(0xdfe6ff, 2.6);
    this.moon.castShadow = true;
    const shadowCamera = this.moon.shadow.camera;
    shadowCamera.left = -SHADOW_EXTENT;
    shadowCamera.right = SHADOW_EXTENT;
    shadowCamera.top = SHADOW_EXTENT;
    shadowCamera.bottom = -SHADOW_EXTENT;
    shadowCamera.near = 1;
    shadowCamera.far = 500;
    this.moon.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    this.moon.shadow.bias = -0.0004;
    this.moon.shadow.normalBias = 0.04;
    this.scene.add(this.moon);
    this.scene.add(this.moon.target);

    // A faint violet rim from the opposite side keeps silhouettes crisp
    this.rim = new THREE.DirectionalLight(0x8a7bff, 0.4);
    this.rim.position.set(0.6, 0.4, -0.7);
    this.scene.add(this.rim);
  }

  createArena() {
    this.arena = new Arena(this.scene);
  }

  createBases(players) {
    // Clear existing bases
    this.bases.forEach(base => base.remove());
    this.bases = [];

    // Create bases for each player
    players.forEach((player, index) => {
      if (player) {
        const position = BASE_POSITIONS[index];
        const color = PLAYER_COLORS[index];
        const base = new Base(this.scene, position, color);
        this.bases.push(base);
      }
    });
  }

  startMatchClock() {
    this.matchClock = 0;
  }

  stopMatchClock() {
    this.matchClock = null;
  }

  // focus: the point the camera is looking at
  update(deltaTime, elapsed, focus, renderer) {
    if (this.matchClock !== null) this.matchClock += deltaTime;

    // The aurora swells and fades on a slow cycle; the sky hue follows it
    const cycle = (elapsed / AURORA_CYCLE_SECONDS) * Math.PI * 2;
    this.ambient.aurora = 0.45 + Math.sin(cycle) * 0.3 + Math.sin(cycle * 2.7) * 0.1;
    this.ambient.hueShift = Math.sin(cycle * 0.5) * 0.5 + 0.5;

    // Keep the shadow box centred on the action, snapped to whole shadow texels
    // so shadow edges don't shimmer as the camera moves
    const texel = (SHADOW_EXTENT * 2) / SHADOW_MAP_SIZE;
    const cx = Math.round(focus.x / texel) * texel;
    const cz = Math.round(focus.z / texel) * texel;
    this.moon.target.position.set(cx, 0, cz);
    this.moon.position.set(cx, 0, cz).addScaledVector(MOON_DIRECTION, 250);

    this.arena.update(deltaTime, elapsed, this.ambient, focus);
    for (const base of this.bases) base.update(elapsed);
  }

  getOccluders() {
    return this.arena.getOccluders();
  }

  getScene() {
    return this.scene;
  }
}
